import {
  SOCIAL_NETWORK_LABELS,
  SOCIAL_NETWORKS,
  type SocialLinks,
  type SocialNetwork,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { SocialIcon } from "@loot-os/ui/components/social-icon";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StorefrontSection } from "../components/StorefrontSection";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/** Where a field starts: `https://` typed for the editor, so a pasted handle reads as a URL. */
const PLACEHOLDERS: Record<SocialNetwork, string> = {
  facebook: "https://www.facebook.com/yourstore",
  x: "https://x.com/yourstore",
  discord: "https://discord.gg/yourstore",
  instagram: "https://www.instagram.com/yourstore/",
  youtube: "https://www.youtube.com/@yourstore",
  tiktok: "https://www.tiktok.com/@yourstore",
};

/** The form's six fields as strings, blank for an unset network. */
type Draft = Record<SocialNetwork, string>;

function toDraft(links: SocialLinks): Draft {
  return Object.fromEntries(SOCIAL_NETWORKS.map((n) => [n, links[n] ?? ""])) as Draft;
}

/** The draft as the mutation takes it: trimmed, and a blank field is a network left out. */
function toLinks(draft: Draft): SocialLinks {
  const links: SocialLinks = {};
  for (const network of SOCIAL_NETWORKS) {
    const url = draft[network].trim();
    if (url) links[network] = url;
  }
  return links;
}

function isSame(a: SocialLinks, b: SocialLinks): boolean {
  return SOCIAL_NETWORKS.every((n) => (a[n] ?? "") === (b[n] ?? ""));
}

/**
 * The social profiles this storefront links from its footer, one URL per network. A blank
 * field means no icon for that network; there is no platform default to inherit, because a
 * store's socials are its own. A whole replace, no optimistic lock: the last save wins.
 */
export function StorefrontSocialLinksPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();

  const save = useMutation(
    trpc.storefront.setSocialLinks.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success("Social links saved.");
      },
    }),
  );

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment } = query.data;

  return (
    <StorefrontSection
      title="Social links"
      description="The social profiles shoppers can reach from the storefront footer, one icon per network."
      environment={environment}
    >
      <Card>
        <CardHeader>
          <CardTitle>Profiles</CardTitle>
          <CardDescription>
            Every link must be an https URL. Leave a network blank and its icon is not shown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SocialLinksForm
            key={JSON.stringify(config.socialLinks)}
            links={config.socialLinks}
            canEdit={can("editor")}
            saving={save.isPending}
            onSave={(links) => save.mutate({ links })}
          />
        </CardContent>
      </Card>
    </StorefrontSection>
  );
}

/** Six URL fields, each with its network's icon; remount with `key` when the saved value changes. */
function SocialLinksForm({
  links,
  canEdit,
  saving,
  onSave,
}: {
  links: SocialLinks;
  canEdit: boolean;
  saving: boolean;
  onSave: (links: SocialLinks) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(links));
  const clean = toLinks(draft);
  const dirty = !isSame(clean, links);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(clean);
  };

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <div className="grid gap-4 sm:grid-cols-2">
        {SOCIAL_NETWORKS.map((network) => (
          <div key={network} className="grid gap-2">
            <Label htmlFor={`social-${network}`} className="flex items-center gap-2">
              <SocialIcon network={network} className="size-4 text-muted-foreground" />
              {SOCIAL_NETWORK_LABELS[network]}
            </Label>
            <Input
              id={`social-${network}`}
              type="url"
              inputMode="url"
              pattern="https://.*"
              title="An https URL"
              value={draft[network]}
              disabled={!canEdit}
              onChange={(e) => setDraft({ ...draft, [network]: e.target.value })}
              placeholder={PLACEHOLDERS[network]}
            />
          </div>
        ))}
      </div>
      {canEdit && (
        <div>
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save social links"}
          </Button>
        </div>
      )}
    </form>
  );
}
